// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the app's tools as an immersive session uses them (the AR
 * refresh, 2026-10-04): `createXrToolsHost` builds the `VrToolsHost` the
 * headset's dock and side panel and a phone's overlay controls read and
 * change, over the same state the browser's panels use — the primary
 * video, the colour display, the pin store, the layer selection, the
 * real-time overlays. main.ts hands it narrow getters and actions; the
 * rules (what the Data, View and Layers pages show, the legend, the
 * search, the track's labels) live here, where they are tested.
 */

import { t, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import type { Dataset } from '../types'
import type { ColorScale } from '../types/color-scale'
import type { PinReadability } from '../ui/dataPinsUI'
import type { MapLayerTintChoice, VrLayersState, VrPinsState, VrRtChoice, VrSatelliteAction, VrSatellitesState, VrViewState } from './vrSidePanel'
import { DEBRIS_GROUPS, type SatelliteLayer } from './satellites/satelliteLayer'
import type { VrDatasetTimeline, VrToolsHost } from './vrSession'
import type { VrPinMark } from './vrPins'
import { MAX_OVERLAYS, tintFor, toggleOverlay, type CatalogLayer, type LayerSelection } from './mapLayers'
import type { PinFrame, PinReading, PinStore } from './measure/pins'
import { buildSeriesChart } from './measure/chartGeometry'
import { trackSegments } from './measure/followExtremes'
import { surfaceLabelKey, type SurfaceMode } from './measure/surfaceMask'
import { referenceLinesOf, referenceLinesVisible, referenceShort, shownReferenceLines, type ReferenceLine } from './referenceLines'
import { pinMarkLabel, pinName } from './measure/pinNames'
import { buildVrLegend, vrLegendFromVector, type VrLegend } from './vrLegend'
import type { VectorLegendModel } from './vector/vectorLegend'
import { DEFAULT_DISPLAY, PALETTE_IDS, displayGradientStops, isAtDefault, isDefaultDisplay, type ColorScaleDisplay, type PaletteId } from './colorScaleDisplay'
import { entryColours, stepLimit, stepOpacity, type RtPlayingEntry } from './overlayDisplay'
import { releaseOf } from '../types/color-scale'
import { dataKindOf } from './datasetTaxonomy'
import { buildSmartIndex, smartSearch, type SmartIndex } from './smartSearch'
import { formatAxisEndLabel, lastFrameMsOf } from './timelineTrackCanvas'
import { rtListed, rtListOrder, type TimeRange } from './rtOverlayList'

export interface XrToolsDeps {
  /** AYNI (satellites plan): the page's Satellites layer, for the Layers page's Satellites section. */
  satellites?(): SatelliteLayer | null
  dataset(): Dataset | null
  datasets(): readonly Dataset[]
  /** The primary video, when a video dataset is on the globe. */
  video(): HTMLVideoElement | null
  setRate(rate: number): void
  /** AYNI (2026-10-20): the speed the viewer chose, which live layers may be holding the video under; absent: the video's own rate. */
  speed?(): number
  stepFrame(direction: -1 | 1): void
  /** Put the video at a time, in seconds. */
  seekVideo(seconds: number): void
  timeline(): VrDatasetTimeline | null
  /** A meeting's presenter has the controls. */
  locked(): boolean
  /**
   * A meeting's presenter has the pins too: `locked()` unless the meeting
   * lends the data tools (a class keeps each participant's own pins).
   * Absent, the pins follow `locked()`.
   */
  pinsLocked?(): boolean
  /** The primary dataset's scale, as its legend and Tools → View read it. */
  scale(): ColorScale | null
  display(): ColorScaleDisplay
  setDisplay(next: ColorScaleDisplay): void
  /** AYNI: what Reset returns to: the dataset's display group, else its own colours. */
  defaultDisplay?(): ColorScaleDisplay
  /** A fresh DOM legend for the dataset on the globe (a phone's legend chip), or null. */
  legendElement(): HTMLElement | null
  /** AYNI (DSA 1.8): the primary globe's vector legends when they replace the dataset's, else null. */
  vectorLegend?(): readonly VectorLegendModel[] | null
  /** AYNI (owner, 2026-10-11): the legend is parked in Tools → View (the browser's Legend switch off). */
  legendParked(): boolean
  setLegendParked(parked: boolean): void
  /** AYNI: the playing Khipu's pins (or a meeting room's), labelled with their notes; shown with the Data tab's. */
  khipuPins?(): readonly VrPinMark[]
  pins: {
    store: PinStore
    readability(): PinReadability
    picking(): boolean
    setPicking(on: boolean): void
    markExtremes(): number
    /** AYNI (owner, 2026-10-18): whether the highest and lowest can follow the data here (not on classes). Absent: they can. */
    canFollow?(): boolean
    /** AYNI (owner, 2026-10-18): everywhere, land only or sea only, once the land and sea tiles exist. */
    surfaceAvailable?(): boolean
    setSurface?(mode: SurfaceMode): void
    frame(): PinFrame | null
    /** A reading as the Data tab prints it. */
    format(reading: PinReading): string
    /** A value on the graph's axis, with units. */
    formatAxis(value: number): string
    /** AYNI (owner, 2026-10-14): the reference lines to draw dashed on the graph (none while hidden). */
    referenceLines?(): readonly ReferenceLine[]
  }
  layerCatalog(): readonly CatalogLayer[] | null
  layerSelection(): LayerSelection
  applyLayers(selection: LayerSelection): void
  /** Every stream that may play over the dataset (not the dataset itself), with its dates and topic; the host lists them as the browser's card does. */
  rtChoices(): ReadonlyArray<VrRtChoice>
  /** AYNI (owner, 2026-11-04): the dataset's dates (null without any) and title, for the list's filter, bars and note. */
  rtBase?(): { range: TimeRange | null; title: string } | null
  rtSelected(): readonly string[]
  rtCap(): number
  pickRt(id: string | null): void
  /** AYNI: the streams playing now, bottom first, with their eyes' state and (2026-10-12) their opacity. */
  rtPlaying(): ReadonlyArray<{ id: string; title: string; hidden: boolean; opacity?: number }>
  setRtHidden(id: string, hidden: boolean): void
  /** AYNI (owner, 2026-10-12): a playing stream's opacity (1 opaque … 0 transparent). */
  setRtOpacity?(id: string, opacity: number): void
  /** AYNI (owner, 2026-10-12): the dataset's name, opacity and eye; null without one. */
  baseLook?(): { title: string; opacity: number; hidden: boolean } | null
  setBaseOpacity?(opacity: number): void
  setBaseHidden?(hidden: boolean): void
  /** The dataset's ✕: it and every overlay off, with Undo. */
  closeBase?(): void
  /** AYNI (2026-10-09): move a playing stream to `to` in the stack (0 is the lowest). */
  moveRt(id: string, to: number): void
  /** AYNI: a playing stream's colours: its scale (null for a picture or classes) and display. */
  rtColours(id: string): RtColours | null
  setRtDisplay(id: string, display: ColorScaleDisplay): void
  recentIds(): string[]
  /** AYNI (owner on Quest, 2026-11-05): the body Browse keeps to (Solar System mode), and lifting it. */
  bodyScope?(): { values: readonly string[]; label: string } | null
  liftBodyScope?(): void
  openBrowse(): void
  closeBrowse(): void
  notify(message: string): void
}

/** The name the viewer gave a pin, or "Highest", "Lowest" or "Pin n", as the Data tab names them. */
export { pinName }

/**
 * AYNI: points across the headset's pin graph (its plot is 930 canvas px
 * wide): a series keeps every frame, and the graph draws the mean of
 * the frames read in each column, as the browser's does.
 */
const VR_CHART_COLUMNS = 465

export function pinWhere(lat: number, lon: number): string {
  const ns = lat >= 0 ? 'N' : 'S'
  const ew = lon >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lon).toFixed(1)}° ${ew}` // i18n-exempt: compass letters and degrees, as the Data tab
}

/** Playing time as m:ss. */
export function clockTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** A time on the pin graph's axis: a date for a dated series, else playing time. */
function axisTime(ms: number, spanMs: number, dated: boolean): string {
  if (!dated) return clockTime(ms / 1000)
  const day = 86_400_000
  if (spanMs > 20 * 365 * day) return formatDate(ms, { year: 'numeric', timeZone: 'UTC' })
  if (spanMs <= 2 * day) return formatDate(ms, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  if (spanMs <= 400 * day) return formatDate(ms, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  return formatDate(ms, { year: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** The Data page's state for a pin store and what the globe can be read for. */
export function buildPinsState(deps: XrToolsDeps['pins'], dated: boolean): VrPinsState {
  const r = deps.readability()
  const readable = r === 'release' || r === 'encoded'
  const list = deps.store.list()
  const pins = list.map((p, i) => ({
    id: p.id,
    color: p.color,
    name: pinName(p, i),
    own: pinName({ tag: p.tag }, i),
    named: !!p.name,
    value: !readable ? '' : p.reading ? deps.format(p.reading) : t('tools.data.pins.nothing'),
    where: pinWhere(p.lat, p.lon),
  }))
  const timed = list.filter((p) => p.series)
  const refs = deps.referenceLines?.() ?? []
  const chart = readable ? buildSeriesChart(timed.map((p) => ({ id: p.id, color: p.color, series: p.series! })), { bridge: 6, columns: VR_CHART_COLUMNS, references: refs.map((r) => r.value) }) : null
  let chartState: VrPinsState['chart'] = null
  if (chart) {
    const series = timed[0]!.series!
    const span = series.endMs - series.startMs
    const frame = deps.frame()
    const now = frame?.timeMs != null && span > 0 ? Math.max(0, Math.min(1, (frame.timeMs - series.startMs) / span)) : null
    chartState = {
      ...chart,
      minLabel: deps.formatAxis(chart.min),
      maxLabel: deps.formatAxis(chart.max),
      startLabel: axisTime(series.startMs, span, dated),
      endLabel: axisTime(series.endMs, span, dated),
      now,
      referenceLabels: refs.map((r) => referenceShort(r)),
    }
  }
  return {
    readable,
    reason: readable ? null : r === 'plan' ? 'plan' : r === 'picture' ? 'picture' : 'none',
    picking: deps.picking(),
    full: deps.store.full,
    max: deps.store.limit,
    pins,
    chart: chartState,
    // AYNI (owner, 2026-10-18): the highest and lowest follow the data, and their track.
    follow: readable && deps.store.marked && (deps.canFollow?.() ?? true)
      ? { on: deps.store.following, track: deps.store.following && deps.store.trackShown }
      : null,
    // AYNI (owner, 2026-10-18): where the highest and lowest are looked for.
    surface: readable && deps.surfaceAvailable?.() ? deps.store.surface : null,
  }
}

/** The View page: the palettes with their ramps on this scale, and the display. */
export function buildViewState(scale: ColorScale | null, display: ColorScaleDisplay, defaults?: ColorScaleDisplay): VrViewState {
  const isDefault = defaults ? isAtDefault(display, defaults) : isDefaultDisplay(display)
  const classified = !!scale && releaseOf(scale)?.kind === 'luma8-classified'
  if (!scale || classified) {
    return { hasScale: false, palettes: [], palette: display.palette, inverted: !!display.inverted, isDefault }
  }
  return {
    hasScale: true,
    palettes: PALETTE_IDS.map((id) => ({
      id,
      label: t(`colorbar.palette.${id}` as MessageKey),
      stops: displayGradientStops(scale, { ...DEFAULT_DISPLAY, palette: id, inverted: display.inverted }, 8)
        .map((s) => `rgb(${s.rgba[0]}, ${s.rgba[1]}, ${s.rgba[2]})`),
    })),
    palette: display.palette,
    inverted: !!display.inverted,
    isDefault,
  }
}

/** AYNI: a playing stream's colours, as the host knows them. */
export interface RtColours {
  /** Null when its colours cannot change (a picture, or classes with their own colours). */
  scale: ColorScale | null
  classified: boolean
  display: ColorScaleDisplay
}

/**
 * AYNI: the streams playing, with what their colour controls need (the
 * ramp and limits, each palette's look for this stream), or why their
 * colours cannot change.
 */
export function withColours(
  playing: ReadonlyArray<{ id: string; title: string; hidden: boolean; opacity?: number }>,
  coloursOf: (id: string) => RtColours | null,
): RtPlayingEntry[] {
  return playing.map((rt) => {
    const c = coloursOf(rt.id)
    if (c?.scale) return { ...rt, colours: entryColours(c.scale, c.display) }
    return { ...rt, colours: null, coloursReason: t(c?.classified ? 'palette.dialog.classes' : 'palette.dialog.picture', { title: rt.title }) }
  })
}

/** AYNI (satellites plan): the Satellites section's state from the layer. */
export function satellitesVrState(layer: SatelliteLayer): VrSatellitesState {
  const s = layer.settings
  let status = ''
  let detail = ''
  if (!layer.onEarth) status = t('satellites.otherBody')
  else if (layer.skyOn && layer.skyStatus) [status, detail = ''] = layer.skyStatus.split('\n')
  else if (layer.status === 'loading') status = t('satellites.status.loading')
  else if (layer.status === 'error') status = t('satellites.status.error', { error: layer.error ?? '' })
  else if (layer.status === 'ready') status = t('satellites.status.count', { shown: formatNumber(layer.visibleCount()), total: formatNumber(layer.engine.count) })
  // Fully VR: the sky's sources, under the count (its own view since 2026-11-03).
  if (layer.fullVrOn) detail = t('satellites.sky.fullVrCredit')
  return {
    on: s.on,
    onEarth: layer.onEarth,
    show: s.show,
    colourBy: s.colourBy,
    countryReady: layer.hasCatalogue,
    altitude: s.altitude,
    trails: s.trails,
    live: layer.clock.live,
    speed: layer.clock.speed,
    status,
    ...(detail ? { detail } : {}),
    stale: layer.stale,
    sky: layer.skyOn,
    skyAvailable: layer.skyAvailable,
    fullVr: layer.fullVrOn,
    ...(layer.skyOn && layer.skyPasses.length ? { passes: layer.skyPasses.map((p) => ({ text: p.text, datasetId: p.datasets[0] ?? null })) } : {}),
  }
}

/** Applies a press in the Satellites section. */
export function applySatelliteAction(layer: SatelliteLayer, action: VrSatelliteAction): void {
  switch (action.kind) {
    case 'sat':
      // The switch is held while another body is on the globe (owner, 2026-10-23).
      if (action.op === 'on') {
        if (layer.onEarth) layer.setOn(!layer.on)
      } else if (action.op === 'trails') layer.update({ trails: !layer.settings.trails })
      else if (action.op === 'live') layer.clock.goLive()
      else if (action.op === 'sky') layer.setSky(!layer.skyOn)
      else if (action.op === 'north') layer.requestNorth()
      else if (action.op === 'fullVr') layer.setFullVr(!layer.fullVrOn)
      else if (action.op === 'debris') {
        const on = layer.settings.show.some((v) => v.endsWith('-debris'))
        const rest = layer.settings.show.filter((v) => !v.endsWith('-debris'))
        layer.update({ show: on ? (rest.length ? rest : ['highlights']) : [...rest, ...DEBRIS_GROUPS] })
      }
      return
    case 'sat-show':
      layer.toggleShow(action.set)
      return
    case 'sat-colour':
      if (action.by === 'country' && !layer.hasCatalogue) return
      layer.update({ colourBy: action.by })
      return
    case 'sat-altitude':
      layer.update({ altitude: action.scale })
      return
    case 'sat-speed':
      layer.clock.setSpeed(action.speed)
      return
  }
}

/** The Layers page from the layer catalogue, the selection and the real-time choices. */
export function buildLayersState(
  catalog: readonly CatalogLayer[] | null,
  selection: LayerSelection,
  rt: ReadonlyArray<VrRtChoice>,
  rtSelected: readonly string[],
  rtCap: number,
  rtPlaying: VrLayersState['rtPlaying'] = [],
  base: VrLayersState['base'] = null,
): VrLayersState {
  const layers = catalog ?? []
  return {
    overlays: layers.filter((l) => l.kind === 'overlay').map((l) => ({ id: l.id, title: l.title })),
    overlaysOn: selection.overlays.map((o) => ({ id: o.id, title: layers.find((l) => l.id === o.id)?.title ?? o.id, tint: o.tint as MapLayerTintChoice })),
    overlayCap: MAX_OVERLAYS,
    rt,
    rtSelected,
    rtCap,
    rtPlaying,
    base,
  }
}

/** The phone track's end labels: the axis's dates, the catalogue's, or the playing time. */
export function trackLabelsFor(
  timeline: VrDatasetTimeline | null,
  dataset: Dataset | null,
  duration: number,
): [string, string] | null {
  if (timeline && timeline.endMs > timeline.startMs) {
    const span = timeline.endMs - timeline.startMs
    const last = timeline.cadenceMs > 0 && timeline.frameCount > 1 ? lastFrameMsOf(timeline) : timeline.endMs
    return [formatAxisEndLabel(timeline.startMs, span, timeline.cadenceMs), formatAxisEndLabel(last, span, timeline.cadenceMs)]
  }
  if (dataset?.startTime && dataset.endTime) {
    const start = Date.parse(dataset.startTime)
    const end = Date.parse(dataset.endTime)
    if (end > start) return [axisTime(start, end - start, true), axisTime(end, end - start, true)]
  }
  if (duration > 0) return [clockTime(0), clockTime(duration)]
  return null
}

export function createXrToolsHost(deps: XrToolsDeps): VrToolsHost {
  let searchIndex: { source: readonly Dataset[]; index: SmartIndex } | null = null
  let legendCache: { key: string; legend: VrLegend | null } | null = null

  const legendKey = (): string => {
    const ds = deps.dataset()
    const scale = ds ? deps.scale() : null
    const vector = deps.vectorLegend?.() ?? null
    return `${ds?.id ?? ''}|${scale ? 's' : (ds?.legendLink ?? '')}|${JSON.stringify(deps.display())}|${deps.legendParked() ? 'parked' : ''}|${vector ? JSON.stringify(vector) : ''}|${referenceLinesVisible() ? 'refs' : ''}`
  }

  const host: VrToolsHost = {
    stepFrame(direction) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.stepFrame(direction)
    },
    playbackRate: () => (deps.video() ? deps.speed?.() ?? deps.video()!.playbackRate : 1),
    setPlaybackRate(rate) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.setRate(rate)
    },
    playbackProgress() {
      const video = deps.video()
      if (!video || !(video.duration > 0) || !Number.isFinite(video.duration)) return null
      return Math.max(0, Math.min(1, video.currentTime / video.duration))
    },
    seekProgress(fraction) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      const video = deps.video()
      if (!video || !(video.duration > 0) || !Number.isFinite(video.duration)) return
      deps.seekVideo(Math.max(0, Math.min(1, fraction)) * video.duration)
    },
    trackLabels() {
      const video = deps.video()
      return trackLabelsFor(deps.timeline(), deps.dataset(), video && Number.isFinite(video.duration) ? video.duration : 0)
    },
    isLocked: () => deps.locked(),
    isLive() {
      const ds = deps.dataset()
      return !!ds && dataKindOf(ds) === 'real-time'
    },
    legend() {
      const key = legendKey()
      if (legendCache?.key === key) return legendCache.legend
      const ds = deps.dataset()
      const scale = ds ? deps.scale() : null
      // AYNI (DSA 1.8): a superseding vector layer's legend first (§8).
      const vector = ds ? deps.vectorLegend?.() ?? null : null
      const legend = vector?.length ? vrLegendFromVector(vector[0])
        : ds && scale ? buildVrLegend(scale, deps.display(), ds.title, shownReferenceLines(ds.id)) : null
      legendCache = { key, legend }
      return legend
    },
    legendKey,
    legendElement: () => deps.legendElement(),
    legendParked: () => deps.legendParked(),
    setLegendParked: (parked) => deps.setLegendParked(parked),
    pinMarks() {
      const pins = deps.pins
      const r = pins.readability()
      const readable = r === 'release' || r === 'encoded'
      // AYNI (owner, 2026-10-18): a following pin's track, up to the frame on the globe.
      const nowMs = pins.store.trackShown ? pins.frame()?.timeMs ?? null : null
      const own = pins.store.list().map((p): VrPinMark => ({
        id: p.id,
        color: p.color,
        lat: p.lat,
        lon: p.lon,
        label: pinMarkLabel(p.name, readable && p.reading ? pins.format(p.reading) : ''),
        ...(pins.store.trackShown && p.follow ? { track: trackSegments(p.series, nowMs) } : {}),
      }))
      const khipu = deps.khipuPins?.() ?? []
      return khipu.length ? [...own, ...khipu] : own
    },
    pins() {
      const ds = deps.dataset()
      const dated = !!deps.timeline() || !!(ds?.startTime && ds.endTime)
      return buildPinsState(deps.pins, dated)
    },
    setPinPicking(on) {
      if (on && (deps.pinsLocked ? deps.pinsLocked() : deps.locked())) return deps.notify(t('vr.notice.presenter'))
      deps.pins.setPicking(on)
    },
    removePin: (id) => deps.pins.store.remove(id),
    renamePin: (id, name) => deps.pins.store.rename(id, name),
    clearPins: () => deps.pins.store.clear(),
    markExtremes() {
      const placed = deps.pins.markExtremes()
      deps.notify(t(placed ? 'tools.data.pins.extremesPlaced' : 'tools.data.pins.extremesNone'))
    },
    toggleFollow() {
      const on = !deps.pins.store.following
      deps.pins.store.setFollow(on)
      deps.notify(t(on ? 'tools.data.pins.followOn' : 'tools.data.pins.followOff'))
    },
    setSurface(mode) {
      if (mode === deps.pins.store.surface) return
      deps.pins.setSurface?.(mode)
      deps.notify(t('tools.data.pins.surface.changed', { where: t(surfaceLabelKey(mode)) }))
    },
    toggleTrack() {
      const store = deps.pins.store
      store.setTrackShown(!store.trackShown)
      deps.notify(t(store.trackShown ? 'tools.data.pins.trackOn' : 'tools.data.pins.trackOff'))
    },
    recentIds: () => deps.recentIds(),
    bodyScope: () => deps.bodyScope?.() ?? null,
    liftBodyScope: () => deps.liftBodyScope?.(),
    search(query) {
      const source = deps.datasets()
      if (!searchIndex || searchIndex.source !== source) searchIndex = { source, index: buildSmartIndex(source) }
      const hits = smartSearch(searchIndex.index, query)
      return hits ? hits.map((h) => h.id) : null
    },
    lockedStream(packageName, title) {
      deps.notify(t('vr.notice.lockedStream', { title, package: packageName }))
    },
    layers: () => {
      const look = deps.baseLook?.() ?? null
      const colours = look ? host.baseColours() : null
      const ramp = colours?.scale ? entryColours(colours.scale, colours.display).ramp : undefined
      // AYNI (owner, 2026-11-04): the browser card's list, by the same rules (rtOverlayList).
      const rtBase = deps.rtBase?.() ?? null
      const selected = deps.rtSelected()
      const { listed, hidden } = rtListed(deps.rtChoices(), rtBase?.range ?? null, selected)
      const state = {
        ...buildLayersState(deps.layerCatalog(), deps.layerSelection(), rtListOrder(listed), selected, deps.rtCap(), withColours(deps.rtPlaying(), (id) => deps.rtColours(id)), look ? { title: look.title, hidden: look.hidden, ...(ramp ? { ramp } : {}) } : null),
        rtBase: rtBase?.range ?? null,
        rtHidden: hidden,
        rtBaseTitle: rtBase?.title ?? '',
      }
      const sats = deps.satellites?.() ?? null
      return sats ? { ...state, satellites: satellitesVrState(sats) } : state
    },
    // AYNI (satellites plan): the Satellites section. Not a room's shared state, so a presenter's lock does not hold it.
    satellite(action) {
      const sats = deps.satellites?.()
      if (sats) applySatelliteAction(sats, action)
    },
    // AYNI (2026-10-09): as in the browser, up to two, each pressed on and
    // off in its own colour; None (null) clears them.
    setOverlay(id) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      const sel = deps.layerSelection()
      const layer = deps.layerCatalog()?.find((l) => l.id === id)
      deps.applyLayers({ ...sel, overlays: id && layer ? toggleOverlay(sel.overlays, id, tintFor(layer)) : [] })
    },
    setTint(id, tint) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      const sel = deps.layerSelection()
      deps.applyLayers({ ...sel, overlays: sel.overlays.map((o) => (o.id === id ? { ...o, tint } : o)) })
    },
    toggleRt(id) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.pickRt(id)
    },
    rtPlaying: () => withColours(deps.rtPlaying(), (id) => deps.rtColours(id)),
    // AYNI (owner, 2026-10-12): the eyes and the opacities are the room's (its lead sends them), so a meeting's presenter locks them.
    setRtHidden(id, hidden) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.setRtHidden(id, hidden)
    },
    setRtOpacity(id, opacity) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.setRtOpacity?.(id, opacity)
    },
    stepRtOpacity(id, dir) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      const rt = deps.rtPlaying().find((r) => r.id === id)
      if (rt) deps.setRtOpacity?.(id, stepOpacity(rt.opacity ?? 1, dir))
    },
    baseLook: () => deps.baseLook?.() ?? null,
    // AYNI (owner, 2026-10-13): the dataset's colours, as its palette button opens them (this viewer's own, as Tools → View's).
    baseColours() {
      const scale = deps.scale()
      if (!deps.baseLook?.()) return null
      const classified = !!scale && releaseOf(scale)?.kind === 'luma8-classified'
      return { scale: classified ? null : scale, classified, display: deps.display() }
    },
    setBaseDisplay: (display) => deps.setDisplay(display),
    setBaseHidden(hidden) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.setBaseHidden?.(hidden)
    },
    setBaseOpacity(opacity) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.setBaseOpacity?.(opacity)
    },
    stepBaseOpacity(dir) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      const look = deps.baseLook?.()
      if (look) deps.setBaseOpacity?.(stepOpacity(look.opacity, dir))
    },
    closeBase() {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.closeBase?.()
    },
    // AYNI (2026-10-09): the order is the room's (its lead sends it), so a
    // meeting's presenter locks it; the colours are this viewer's own view.
    moveRt(id, to) {
      if (deps.locked()) return deps.notify(t('vr.notice.presenter'))
      deps.moveRt(id, to)
    },
    rtColours: (id) => deps.rtColours(id),
    setRtDisplay: (id, display) => deps.setRtDisplay(id, display),
    setRtPalette(id, palette) {
      const c = deps.rtColours(id)
      if (c?.scale && PALETTE_IDS.includes(palette as PaletteId)) deps.setRtDisplay(id, { ...c.display, palette: palette as PaletteId })
    },
    stepRtLimit(id, end, dir) {
      const c = deps.rtColours(id)
      if (!c?.scale) return
      const next = stepLimit(c.display, end, dir)
      if (next !== c.display) deps.setRtDisplay(id, next)
    },
    setRtInverted(id, on) {
      const c = deps.rtColours(id)
      if (c?.scale && !!c.display.inverted !== on) deps.setRtDisplay(id, { ...c.display, inverted: on })
    },
    view() {
      const legend = host.legend()
      const look = deps.baseLook?.() ?? null
      // AYNI (owner, 2026-10-14): the reference lines' switch, for a dataset that has any.
      const references = referenceLinesOf(deps.dataset()?.id).length ? referenceLinesVisible() : null
      return { ...buildViewState(deps.scale(), deps.display(), deps.defaultDisplay?.()), legend, legendParked: !!legend && deps.legendParked(), references, ...(look ? { opacity: look.opacity } : {}) }
    },
    setPalette(id) {
      deps.setDisplay({ ...deps.display(), palette: id as PaletteId })
    },
    setInverted(on) {
      deps.setDisplay({ ...deps.display(), inverted: on })
    },
    resetView() {
      deps.setDisplay(deps.defaultDisplay ? deps.defaultDisplay() : { ...DEFAULT_DISPLAY })
    },
    openBrowse: () => deps.openBrowse(),
    closeBrowse: () => deps.closeBrowse(),
  }
  return host
}
